/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  AuthenticationMethodDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";

import { t } from "i18next";
import { reaction, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { isTruthy } from "remeda";
import { Skeleton } from "../../../../../generated/images";
import type { AuthAttempt } from "../../../../auth";
import { authDispatcher } from "../../../../auth";
import { useEngineClient } from "../../../../services/engineClient";
import { updateTriggerDescription } from "../../../../services/updateDescription";
import type { CandidateStore } from "../../../../stores/automation/candidate.store";
import * as TriggerStoreMerger from "../../../../stores/automation/mergers/trigger";
import { useRootStore } from "../../../../stores/hooks";
import type {
  InputCandidate,
  TriggerCandidate,
} from "../../../../types/candidate";
import { applicationIdInput } from "../../../../utils/application-id-input";
import {
  INPUT_APPLICATION_ID,
  isSmartSuiteIntegration,
  WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
  ActionLabel,
} from "../../../../utils/constants";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../utils/inputs";
import { InputForm } from "../../Forms/InputForm";
import { CredentialSelector } from "../../Selectors/CredentialSelector";
import { TriggerSelector } from "../../Selectors/TriggerSelector";
import {
  SectionHeading,
  StyledSelectTriggerContainer,
  StyledTriggerInputsContainer,
} from "../../style";
import { ConditionsForm } from "./ConditionsForm";
import { buildInitialTriggerDescription } from "./initial-trigger-description";
import { TwirpError, TwirpErrorCode } from "@smartsuite-foundry/twirp-ts";
import * as R from "remeda";
import { InfoSection } from "../../../../components/InfoSection";

const TRIGGERS_WITH_APPLICATION = [
  "created-record",
  "updated-record",
  "matches-condition",
  "form-submitted",
  "webhook",
  WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
];

const TRIGGERS_WITHOUT_CONDITIONS: string[] = ["schedule", "webhook"];

export const TriggerSection = observer<{
  setAuthAttempt: (auth: AuthAttempt) => void;
  candidate: CandidateStore;
}>(({ setAuthAttempt }) => {
  const store = useRootStore();
  const engineClient = useEngineClient();
  const [loading, setLoading] = useState<boolean>(false);
  const { candidate } = store.automations;
  const { trigger } = candidate.self;
  const [isSelectOpened, setIsSelectOpened] = useState(false);
  const [triggerAppId, setTriggerAppId] = useState<string>();

  const onClickSelect = useCallback(() => {
    setIsSelectOpened(!isSelectOpened);
  }, [isSelectOpened]);
  const onClickOutsideSelect = useCallback(() => {
    setIsSelectOpened(false);
  }, [setIsSelectOpened]);

  const updateDescription = useCallback(
    async (conditionListCleanup: boolean): Promise<void> => {
      store.automations.candidate.clearTriggerPermissionsError();
      store.automations.setLoadingDynamicDescription(true);
      try {
        await updateTriggerDescription(
          candidate,
          conditionListCleanup,
          engineClient,
          store.automations,
          store.integrations,
        );
        store.automations.candidate.clearTriggerPermissionsError();
      } catch (error) {
        if (error instanceof TwirpError) {
          if (error.code === TwirpErrorCode.PermissionDenied) {
            store.automations.candidate.setTriggerPermissionsError(
              error.message,
            );
          }
        }
      }
      store.automations.setLoadingDynamicDescription(false);
      store.automations.setRefreshingList(false);
    },
    [candidate, engineClient, store.applications?.selectedApplicationId],
  );

  useEffect(() => {
    store.automations.candidate.clearTriggerPermissionsError();
    const removeReaction = reaction(
      () => candidate.self.trigger.credentialId,
      (credentialId, previousCredentialId) => {
        if (
          isTruthy(credentialId) &&
          credentialId !== previousCredentialId &&
          !TRIGGERS_WITH_APPLICATION.includes(
            trigger.triggerReference?.triggerId ?? "",
          )
        ) {
          void updateDescription(false);
        }
      },
    );
    return () => removeReaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isSmartSuiteIntegration(trigger.triggerReference!.integrationId) &&
      !isTruthy(trigger.credentialId)
    ) {
      const integration = store.integrations.list.find(
        (integration: IntegrationDescription) =>
          integration.integrationId === trigger.triggerReference!.integrationId,
      );

      if (isTruthy(integration)) {
        const authMethod = integration.authenticationMethods.find(
          (auth: AuthenticationMethodDescription) =>
            auth.authenticationMethodId === "ss-auth",
        );

        if (isTruthy(authMethod)) {
          const authAttempt: AuthAttempt = {
            integrationId: trigger.triggerReference!.integrationId,
            authMethod,
            target: {
              type: "trigger",
              hash: trigger.hash,
            },
          };

          void authDispatcher(authAttempt, [], store, engineClient).then(() => {
            setLoading(false);
          });
        }
      }
    }
  }, [
    engineClient,
    store,
    trigger.credentialId,
    trigger.hash,
    trigger.triggerReference,
  ]);

  useEffect(() => {
    if (
      trigger.inputs.length > 0 &&
      containsAppId(trigger.triggerReference?.triggerId ?? "") &&
      !isAppSet(trigger)
    ) {
      // TODO: REMOVE FOLLOWING IF IN NEXT RELEASE
      if (!triggerAppId && !store.applications?.selectedApplicationId) {
        return;
      }
      candidate.setTrigger(
        TriggerStoreMerger.mergeInputById(
          applicationIdInput(
            triggerAppId ?? store.applications?.selectedApplicationId ?? "",
          ),
        ),
      );
    } else {
      setInitialFields();
    }
  }, [
    candidate,
    store.applications?.selectedApplicationId,
    trigger.triggerReference,
    trigger.inputs,
    updateDescription,
  ]);

  useEffect(() => {
    const applicationId = findInputAndPick(
      trigger.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );
    if (applicationId !== undefined) {
      setTriggerAppId(String(applicationId));
    }
  }, [trigger.triggerReference?.triggerId, trigger.inputs]);

  useEffect(() => {
    if (
      isTruthy(candidate.self.trigger.credentialId) &&
      // making sure we do not load trigger description on just clicking on loaded trigger area again.
      candidate.self.trigger.description.outputs.length === 0 &&
      candidate.self.trigger.description.recordListOutput === undefined
    ) {
      void updateDescription(false);
    }
  }, [candidate.self.trigger.credentialId]);

  useEffect(() => {
    if (store.credentials.isLoaded) {
      if (candidate.self.trigger.credentialId) {
        const credential = store.credentials.list.find(
          (item) => item.credentialId === candidate.self.trigger.credentialId,
        );
        if (!credential) {
          candidate.setTrigger(TriggerStoreMerger.mergeCredentialId(""));
        }
      }
    }
  }, [store.credentials.list]);

  const setInitialFields = (): void => {
    if (
      trigger.inputs.length === 0 &&
      isSmartSuiteIntegration(trigger.triggerReference!.integrationId)
    ) {
      setLoading(true);
      const integration = store.integrations.list.find(
        (item) =>
          item.integrationId === trigger.triggerReference?.integrationId,
      );
      const triggerDescription = integration?.triggers.find(
        (item) => item.triggerId === trigger.triggerReference?.triggerId,
      );

      if (triggerDescription !== undefined) {
        if (containsAppId(triggerDescription.triggerId)) {
          const description = buildInitialTriggerDescription(
            triggerDescription,
            store.applications.list,
          );
          candidate.setTrigger(
            TriggerStoreMerger.mergeTriggerDescription(description),
            TriggerStoreMerger.mergeInputDescriptionList(
              description.inputs,
              false,
            ),
            TriggerStoreMerger.mergeInputById(
              applicationIdInput(
                triggerAppId ?? store.applications?.selectedApplicationId ?? "",
              ),
            ),
          );
        }
      }
    }
  };

  const containsAppId = (triggerId: string): boolean =>
    [
      "created-record",
      "updated-record",
      "matches-condition",
      "schedule",
      "form-submitted",
      WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
    ].includes(triggerId);

  const onRemoveCredential = (credentialId: string): void => {
    void engineClient
      .deleteCredential({
        credentialId,
        solutionId: store.config.solutionId,
      })
      .then(() => {
        if (trigger.credentialId === credentialId) {
          candidate.setTrigger(TriggerStoreMerger.mergeCredentialId(""));
        }
        store.credentials.removeItem(credentialId);
      });
  };

  const inputFormOnChange = (input: InputCandidate, index: number): void => {
    candidate.setTrigger(TriggerStoreMerger.mergeInputById(input));
    const inputDescription = trigger.description.inputs.find(
      (inputDescription) => inputDescription.inputId === input.inputId,
    );
    if (input.inputId === INPUT_APPLICATION_ID) {
      candidate.setActionsWithOutReferences();
      // Switching the trigger's table invalidates its condition fields too.
      candidate.branches.clearConditionsIfTriggerReference();
      // PDF actions fed by the trigger pick a template of the table it
      // watches, so they lose theirs and describe themselves again.
      candidate.refreshTriggerDependencies();
      // Update dynamic descriptions for DocsAutomator actions when application ID changes
      const actions = candidate.allActions;
      actions.forEach((action) => {
        if (
          action.actionReference?.actionId === ActionLabel.docsautomator &&
          action.needToUpdateDynamicDescription !== true
        ) {
          candidate.setNeedToUpdateDynamicDescription(action, true);
        }
      });
    }
    if (inputDescription?.dynamic) {
      void updateDescription(true);
    }
  };

  const automationHasError = (): boolean =>
    candidate.self.systemStatus
      ? candidate.self.systemStatus.errors.length > 0
      : false;

  const needToShowError = (): boolean => {
    return candidate.isTriggerSubmitted || automationHasError();
  };

  const isConditionsNeeded = (): boolean =>
    !TRIGGERS_WITHOUT_CONDITIONS.includes(
      candidate.self.trigger.triggerReference?.triggerId ?? "",
    );

  return (
    <>
      {Boolean(trigger.triggerReference!.integrationId) &&
        trigger.triggerDisabled !== true &&
        trigger.integrationDisabled !== true && (
          <>
            <SectionHeading>
              {t("automation-form.trigger-settings")}
            </SectionHeading>
            {/* Select trigger */}
            <StyledSelectTriggerContainer>
              <TriggerSelector
                automationId={candidate.self.automationId}
                trigger={toJS(trigger)}
                integrations={toJS(store.integrations.list)}
                isOpen={isSelectOpened}
                onClick={onClickSelect}
                onClickOutside={onClickOutsideSelect}
                onChange={(integrationId, triggerId) => {
                  setIsSelectOpened(false);
                  if (
                    triggerId ===
                    candidate.self.trigger?.triggerReference?.triggerId
                  ) {
                    return;
                  }
                  store.modal.openConfirmModal({
                    type: "warning",
                    title: t("confirm-modal.are-you-sure"),
                    description: `${t("confirm-modal.want-change-trigger")} ${t(
                      "confirm-modal.this-may-invalidate-trigger",
                    )}. ${t("confirm-modal.cannot-undone-action")}.`,
                    positiveButtonText: t("confirm-modal.yes-change-trigger"),
                    negativeButtonText: t("confirm-modal.no-cancel"),
                    onClose: () => {
                      store.modal.closeConfirmModal();
                    },
                    onConfirm: () => {
                      candidate.setTrigger(
                        TriggerStoreMerger.mergeReferenceIds(
                          integrationId,
                          triggerId,
                        ),
                      );
                      candidate.setActionsWithOutReferences();
                      candidate.looping.clearSourceIfTriggerReference();
                      candidate.branches.clearConditionsIfTriggerReference();
                      store.modal.closeConfirmModal();
                    },
                  });
                }}
              />
            </StyledSelectTriggerContainer>

            {Boolean(trigger.triggerReference!.triggerId) && (
              <>
                {/* Select credential */}
                {!isSmartSuiteIntegration(
                  trigger.triggerReference!.integrationId,
                ) && (
                  <StyledSelectTriggerContainer>
                    <CredentialSelector
                      isSubmitted={needToShowError()}
                      integrationId={trigger.triggerReference?.integrationId}
                      credentialId={trigger.credentialId}
                      integrations={toJS(store.integrations.list)}
                      credentials={toJS(store.credentials.list)}
                      onChange={(credentialId) => {
                        candidate.setTrigger(
                          TriggerStoreMerger.mergeCredentialId(credentialId),
                        );
                      }}
                      onAdd={(authMethod) => {
                        const authAttempt: AuthAttempt = {
                          integrationId:
                            trigger.triggerReference!.integrationId,
                          authMethod,
                          target: {
                            type: "trigger",
                            hash: trigger.hash,
                          },
                        };
                        if (authMethod.type.oneofKind === "inputs") {
                          setAuthAttempt(authAttempt);
                        } else {
                          void authDispatcher(
                            authAttempt,
                            [],
                            store,
                            engineClient,
                          );
                        }
                      }}
                      onRemove={onRemoveCredential}
                      loading={store.automations.isAllDynamicDescriptionsLoaded()}
                    />
                  </StyledSelectTriggerContainer>
                )}

                {
                  <>
                    {R.isTruthy(trigger.description.info?.details) && (
                      <InfoSection info={trigger.description.info} />
                    )}
                    {/* Trigger inputs */}
                    <StyledTriggerInputsContainer theme={{ loading }}>
                      {trigger.inputs.map((input, index) => (
                        <InputForm
                          key={input.inputId}
                          input={input}
                          descriptions={trigger.description.inputs}
                          trigger={candidate.self.trigger}
                          actions={candidate.allActions}
                          actionHash=""
                          integrations={[]}
                          loading={loading}
                          showValidationError={needToShowError()}
                          onChange={(input) => inputFormOnChange(input, index)}
                        />
                      ))}
                    </StyledTriggerInputsContainer>

                    {/* Trigger conditions */}
                    {!loading && isConditionsNeeded() && (
                      <ConditionsForm
                        candidate={candidate}
                        integrations={store.integrations.list}
                      />
                    )}
                    {!store.automations.isAllDynamicDescriptionsLoaded() &&
                      !candidate.self.trigger.description.triggerId && (
                        <Skeleton />
                      )}
                  </>
                }
              </>
            )}
          </>
        )}
    </>
  );
});
TriggerSection.displayName = "TriggerSection";

const isAppSet = (trigger: TriggerCandidate): boolean => {
  const applicationId = trigger.inputs.find(
    (input) => input.inputId === INPUT_APPLICATION_ID,
  );
  const value = applicationId?.value?.values[0]?.value;
  return value?.oneofKind === "string" && isTruthy(value.string);
};
